import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Trees, Shield, Clock, Phone, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { ModernLogo } from "@/components/ModernLogo";
import landscaperImage from "@/assets/real-landscaper.jpg";

export const LandscapingPage = () => {
  return (
    <div className="min-h-screen bg-gradient-subtle">
      <SEO
        title="Professional Landscaping Services - Expert Landscapers | Sortable"
        description="Find qualified landscapers and gardeners near you. Garden design, lawn care, tree services, paving, and outdoor maintenance. Fully insured professionals across the UK."
        keywords="landscaper, landscaping services, garden design, lawn care, tree services, hedge trimming, paving, decking, garden maintenance, fence installation, turfing, planting, irrigation, outdoor lighting, garden clearance, patio installation, driveway installation, artificial grass, tree removal, stump grinding, local landscaper"
        canonical="https://sortable.lovable.app/services/landscaping"
        ogImage="https://sortable.lovable.app/real-landscaper.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Professional Landscaping Services",
          "description": "Garden design and landscaping services by qualified professionals",
          "provider": {
            "@type": "Organization",
            "name": "Sortable"
          },
          "serviceType": "Landscaping Services",
          "areaServed": {
            "@type": "Country",
            "name": "United Kingdom"
          }
        }}
      />

      {/* Header */}
      <header className="bg-background/95 backdrop-blur-sm border-b border-border/50 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-2 md:py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <ModernLogo showTagline={false} />
            </Link>
            <div className="flex items-center gap-1 md:gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <span className="hidden md:inline">Home</span>
                  <span className="md:hidden">Home</span>
                </Button>
              </Link>
              <Link to="/for-tradesmen">
                <Button variant="outline" size="sm">
                  <span className="hidden md:inline">For Tradesmen</span>
                  <span className="md:hidden">Tradesmen</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-green-600/10 border border-green-600/20 text-green-600 px-6 py-3 rounded-full text-lg font-semibold mb-8 shadow-elegant">
            <Trees className="h-5 w-5" />
            Professional Landscaping Services
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-hero bg-clip-text text-transparent">
            Expert Landscapers & Garden Designers
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Transform your outdoor spaces with professional landscaping services. From garden design to lawn care, find qualified landscapers for all your outdoor projects.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/create-job">
              <Button size="lg" className="bg-gradient-hero hover:shadow-glow transform hover:scale-105">
                <Users className="mr-2 h-5 w-5" />
                Find Landscapers Now
              </Button>
            </Link>
            <Link to="/for-tradesmen">
              <Button size="lg" variant="outline">
                <Trees className="mr-2 h-5 w-5" />
                Join as a Landscaper
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 px-4 bg-background/50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Complete Landscaping Services
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our network of qualified landscapers provides comprehensive outdoor solutions for residential and commercial properties.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Garden Design",
                description: "Professional garden design and planning services for all outdoor spaces",
                icon: <Trees className="w-6 h-6" />
              },
              {
                title: "Lawn Care & Turfing",
                description: "Lawn installation, maintenance, and artificial grass installation",
                icon: <CheckCircle className="w-6 h-6" />
              },
              {
                title: "Paving & Patios",
                description: "Patio installation, driveway paving, and decorative stonework",
                icon: <Shield className="w-6 h-6" />
              },
              {
                title: "Tree Services",
                description: "Tree removal, pruning, hedge trimming, and stump grinding",
                icon: <Trees className="w-6 h-6" />
              },
              {
                title: "Fencing & Decking",
                description: "Fence installation, repair, and custom decking solutions",
                icon: <CheckCircle className="w-6 h-6" />
              },
              {
                title: "Garden Maintenance",
                description: "Regular garden maintenance, seasonal cleanup, and plant care",
                icon: <Clock className="w-6 h-6" />
              }
            ].map((service, index) => (
              <Card key={index} className="border-border/50 hover:shadow-elegant transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-600/10 rounded-lg text-green-600">
                      {service.icon}
                    </div>
                    <CardTitle className="text-lg">{service.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {service.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose Our Landscaping Network?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              All our landscapers are experienced professionals committed to creating beautiful outdoor spaces.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Expert Design</h4>
                    <p className="text-muted-foreground">
                      Professional garden design services tailored to your space and lifestyle needs.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Quality Materials</h4>
                    <p className="text-muted-foreground">
                      Premium plants, materials, and equipment for lasting beauty and functionality.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Seasonal Expertise</h4>
                    <p className="text-muted-foreground">
                      Year-round garden care and maintenance adapted to seasonal requirements.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Fully Insured</h4>
                    <p className="text-muted-foreground">
                      Comprehensive insurance coverage and guarantees on all landscaping work.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src={landscaperImage} 
                alt="Professional landscaper working on garden design"
                className="rounded-lg shadow-lg w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Ready to Transform Your Garden?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Post your landscaping project and get quotes from qualified, local landscapers. Professional garden design and maintenance guaranteed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/create-job">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="bg-white text-primary hover:bg-gray-100 font-semibold px-8 py-3 border-white"
                >
                  <Users className="mr-2 h-5 w-5" />
                  Get Quotes Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-background border-t py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <ModernLogo showTagline={false} />
              <p className="text-muted-foreground mt-4 max-w-md">
                Connecting you with qualified landscapers across the UK for all your garden needs.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li>Garden Design</li>
                <li>Lawn Care</li>
                <li>Paving & Patios</li>
                <li>Tree Services</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Customers</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/" className="hover:text-primary">Home</Link></li>
                <li><Link to="/how-it-works-customer" className="hover:text-primary">How It Works</Link></li>
                <li><Link to="/create-job" className="hover:text-primary">Post a Job</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Landscapers</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/for-tradesmen" className="hover:text-primary">Join Our Network</Link></li>
                <li><Link to="/tradesman-registration" className="hover:text-primary">Register Now</Link></li>
                <li><Link to="/how-it-works" className="hover:text-primary">How It Works</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};